import { useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { FormRow } from '@gravity-ui/components';
import { ArrowLeft, ChevronDown, ChevronUp, CircleInfoFill } from '@gravity-ui/icons';
import { Button, Icon, TextArea } from '@gravity-ui/uikit';
import { zodResolver } from '@hookform/resolvers/zod';
import { skipToken } from '@reduxjs/toolkit/query';

import { useSafeParams } from 'hooks';
import { useGetBlankQuery } from 'store/api';
import type { Problem } from 'store/types/common';
import { getLatestByDateAndId } from 'shared/utils/get-latest-by-date-and-id';

import {
	ACHIEVEMENTS,
	BACK,
	DEADLINES,
	EVALUATE_ACTIVITY,
	EXAMPLE_GOAL,
	FINAL_SAVE,
	GOAL,
	GOAL_PLAN,
	GOAL_TITLE,
	HINTS,
	HINTS_QUESTIONS,
	PLACEHOLDER_GOAL,
	RESULT,
	SATISFACTION_ACTIVITY,
	SCALE,
	SCALE_SATISFACTION,
	STEPS_PLAN,
	TITLE_THREE,
} from '../constants';

import { Loader } from './utils/load-skeleton';
import { ProblemAssessmentForm, ResultScoreSelect } from './utils/problem-assessment-form';
import type { StepThreeInfo } from './utils/use-validation';
import { useStepThreeValidation } from './utils/use-validation';
import { useMediaQuery } from '@mui/material';
import styles from './styles.module.scss';

export const StepThreeForm = ({ active, prevStep, onStepComplete, isMobile }: any) => {
	const [isHelpUpperVisible, setHelpUpperVisible] = useState(true);
	const [isHelpLowerVisible, setHelpLowerVisible] = useState(true);

	const isAdaptive = useMediaQuery('(max-width: 953px)');

	const { clientId } = useSafeParams();

	const { data, isLoading } = useGetBlankQuery(clientId ?? skipToken, {
		refetchOnMountOrArgChange: 1,
	});

	const problems = data?.problems || [];
	// Находим patientEstimate с самой поздней датой (при равных датах - с большим ID)
	const patientEstimate = getLatestByDateAndId(data?.patientEstimates);

	const sortedProblems = useMemo(() => {
		return (
			problems
				?.slice()
				.sort((a, b) => b.importance - a.importance)
				.slice(0, 5) || []
		);
	}, [problems]);

	const { stepThreeSchema } = useStepThreeValidation();

	const methods = useForm<StepThreeInfo>({
		resolver: zodResolver(stepThreeSchema),
		defaultValues: {
			problems: sortedProblems.map((problem: Problem) => {
				// Находим estimate с самой поздней датой (при равных датах - с большим ID)
				const estimate = getLatestByDateAndId(problem.estimate);
				return {
					id: problem.id,
					execution: estimate?.execution ? String(estimate.execution) : '',
					satisfaction: estimate?.satisfaction ? String(estimate.satisfaction) : '',
					prefix: estimate?.prefix ? [estimate.prefix] : [],
					domainCode: estimate?.domainCode ? estimate.domainCode.split('') : [],
					shortTermGoals:
						problem.shortTermGoals?.map((goal) => ({
							resultScore: goal.resultScore || '',
						})) || [],
				};
			}),
			goal: patientEstimate?.goal || '',
			goalPlan: patientEstimate?.goalPlan || '',
			scoreResult: patientEstimate?.scoreResult || '',
		},
		mode: 'onSubmit',
	});

	useEffect(() => {
		if (data) {
			console.log('шаг 3', data);
		}
	}, [data]);

	const {
		handleSubmit,
		formState: { isSubmitted, errors },
	} = methods;

	const toggleHelpUpperVisibility = () => {
		setHelpUpperVisible(!isHelpUpperVisible);
	};

	const toggleHelpLowerVisibility = () => {
		setHelpLowerVisible(!isHelpLowerVisible);
	};

	const onSubmit = (data: StepThreeInfo) => {
		onStepComplete(data);
	};
	useEffect(() => {
		if (data) {
			// Находим patientEstimate с самой поздней датой (при равных датах - с большим ID)
			const patientEstimate = getLatestByDateAndId(data.patientEstimates);
			methods.reset({
				problems: sortedProblems.map((problem: Problem) => {
					// Находим estimate с самой поздней датой (при равных датах - с большим ID)
					const estimate = getLatestByDateAndId(problem.estimate);
					return {
						id: problem.id,
						execution: estimate?.execution ? String(estimate.execution) : '',
						satisfaction: estimate?.satisfaction ? String(estimate.satisfaction) : '',
						prefix: estimate?.prefix ? [estimate.prefix] : [],
						domainCode: estimate?.domainCode ? estimate.domainCode.split('') : [],
						shortTermGoals:
							problem.shortTermGoals?.map((goal) => ({
								resultScore: goal.resultScore || '',
							})) || [],
					};
				}),
				goal: patientEstimate?.goal || '',
				goalPlan: patientEstimate?.goalPlan || '',
				scoreResult: patientEstimate?.scoreResult || '',
			});
		}
	}, [data, methods, sortedProblems]);

	return (
		<>
			{isLoading && <Loader />}

			{!isLoading && (
				<>
					{isAdaptive ? (
						<div className={styles.container}>
							{isMobile ? (
								<>
									<div className={styles.info__title}>
										<h3>{TITLE_THREE}</h3>
										{/* <Label theme="info" size="m">
												{ASSESSMENT}
											</Label> */}
									</div>

									<div className={`${styles.help} ${isHelpUpperVisible ? '' : styles.hidden}`}>
										<div className={styles.help__title}>
											<CircleInfoFill className={styles.help__title__svg} />
											<h4>{HINTS}</h4>
											<Button onClick={toggleHelpUpperVisibility} view="flat">
												{' '}
												{isHelpUpperVisible ? <ChevronUp /> : <ChevronDown />}
											</Button>
										</div>
										<div
											className={`${styles.help__list} ${isHelpUpperVisible ? '' : styles.hidden}`}
										>
											<div>
												<h4>{EVALUATE_ACTIVITY}</h4>
												<span>{SCALE}</span>
												<h4>{SATISFACTION_ACTIVITY}</h4>
												<span>{SCALE_SATISFACTION}</span>
											</div>
										</div>
									</div>
								</>
							) : (
								''
							)}
							<div className={styles.clientRequestBlock}>
								<FormRow label="Запрос клиента" className={styles.row} required>
									<TextArea
										value={data?.customerRequest || ''}
										minRows={6}
										note={<span>{(data?.customerRequest || '').length}/2000</span>}
										readOnly
										className={styles.clientRequestTextArea}
									/>
								</FormRow>

								<FormProvider {...methods}>
									<form onSubmit={handleSubmit(onSubmit)}>
										<div className={styles.info}>
											{isMobile ? (
												''
											) : (
												<>
													<div className={styles.info__title}>
														<h3>{TITLE_THREE}</h3>
														{/* <Label theme="info" size="m">
												{ASSESSMENT}
											</Label> */}
													</div>

													<div
														className={`${styles.help} ${isHelpUpperVisible ? '' : styles.hidden}`}
													>
														<div className={styles.help__title}>
															<CircleInfoFill className={styles.help__title__svg} />
															<h4>{HINTS}</h4>
															<Button onClick={toggleHelpUpperVisibility} view="flat">
																{' '}
																{isHelpUpperVisible ? <ChevronUp /> : <ChevronDown />}
															</Button>
														</div>
														<div
															className={`${styles.help__list} ${isHelpUpperVisible ? '' : styles.hidden}`}
														>
															<div>
																<h4>{EVALUATE_ACTIVITY}</h4>
																<span>{SCALE}</span>
																<h4>{SATISFACTION_ACTIVITY}</h4>
																<span>{SCALE_SATISFACTION}</span>
															</div>
														</div>
													</div>
												</>
											)}

											{sortedProblems.map((problem: Problem, index: number) => (
												<ProblemAssessmentForm
													key={problem.id || index}
													index={index}
													problemText={problem.text}
													problemImportance={problem.importance}
													shortTermGoals={problem.shortTermGoals || []}
													isMobile={isMobile}
												/>
											))}

											<div
												className={`${styles.help} ${isHelpLowerVisible ? '' : styles.hidden}`}
											>
												<div className={styles.help__title}>
													<CircleInfoFill className={styles.help__title__svg} />
													<h4>{HINTS}</h4>
													<Button onClick={toggleHelpLowerVisibility} view="flat">
														{' '}
														{isHelpLowerVisible ? <ChevronUp /> : <ChevronDown />}
													</Button>
												</div>
												<div
													className={`${styles.help__list} ${isHelpLowerVisible ? '' : styles.hidden}`}
												>
													<ol>
														{HINTS_QUESTIONS}
														<ul>
															<li>{ACHIEVEMENTS}</li>
															<li>{RESULT}</li>
															<li>{DEADLINES}</li>
														</ul>
													</ol>
												</div>
											</div>

											<h4>{GOAL_TITLE}</h4>
											<div className={styles.goalSection}>
												<FormRow
													label={GOAL}
													className={`${styles.info__row} ${styles.top}`}
													required
												>
													<TextArea
														size="xl"
														hasClear
														minRows={4}
														placeholder={PLACEHOLDER_GOAL}
														note={<span>{EXAMPLE_GOAL}</span>}
														{...methods.register('goal')}
														validationState={
															isSubmitted && errors.goal ? 'invalid' : undefined
														}
														errorPlacement="inside"
														errorMessage={
															isSubmitted && errors.goal
																? 'Обязательно для заполнения'
																: undefined
														}
													/>
												</FormRow>

												<div className={styles.scoreFieldMobile}>
													<Controller
														name="scoreResult"
														control={methods.control}
														render={({ field }) => (
															<ResultScoreSelect
																value={field.value as string}
																placeholder="Выберите оценку"
																onChange={field.onChange}
															/>
														)}
													/>
												</div>
											</div>

											<FormRow label={GOAL_PLAN} className={`${styles.info__row} ${styles.top}`}>
												<TextArea
													size="xl"
													hasClear
													minRows={4}
													placeholder={STEPS_PLAN}
													{...methods.register('goalPlan')}
													validationState={
														isSubmitted && errors.goalPlan ? 'invalid' : undefined
													}
													errorPlacement="inside"
													errorMessage={
														isSubmitted && errors.goalPlan
															? 'Обязательно для заполнения'
															: undefined
													}
												/>
											</FormRow>

											<div className={styles.info__btn_group}>
												{active !== 0 && (
													<>
														{isAdaptive ? (
															<Button onClick={prevStep} size="xl">
																<Icon data={ArrowLeft} size={18} />
															</Button>
														) : (
															<Button onClick={prevStep} size="xl">
																<Icon data={ArrowLeft} size={18} />
																{BACK}
															</Button>
														)}
													</>
												)}
												<Button
													size="xl"
													type="submit"
													//disabled={!isValid}
													// view={isValid ? 'action' : 'raised'}
													className={styles.info__btn_group__save}
												>
													{FINAL_SAVE}
												</Button>
											</div>
										</div>
									</form>
								</FormProvider>
							</div>
						</div>
					) : (
						<div className={styles.container}>
							<div className={styles.clientRequestBlock}>
								<FormRow label="Запрос клиента" className={styles.row} required>
									<TextArea
										value={data?.customerRequest || ''}
										minRows={6}
										note={<span>{(data?.customerRequest || '').length}/2000</span>}
										readOnly
										className={styles.clientRequestTextArea}
									/>
								</FormRow>

								<FormProvider {...methods}>
									<form onSubmit={handleSubmit(onSubmit)}>
										<div className={styles.info}>
											<div className={styles.info__title}>
												<h3>{TITLE_THREE}</h3>
												{/* <Label theme="info" size="m">
												{ASSESSMENT}
											</Label> */}
											</div>

											{sortedProblems.map((problem: Problem, index: number) => (
												<ProblemAssessmentForm
													key={problem.id || index}
													index={index}
													problemText={problem.text}
													problemImportance={problem.importance}
													shortTermGoals={problem.shortTermGoals || []}
													isMobile={isMobile}
												/>
											))}

											<h4>{GOAL_TITLE}</h4>
											<div className={styles.goalSectionDesktop}>
												<FormRow
													label={GOAL}
													className={`${styles.info__row} ${styles.top} ${styles.goalField}`}
													required
												>
													<TextArea
														size="xl"
														hasClear
														minRows={4}
														placeholder={PLACEHOLDER_GOAL}
														note={<span>{EXAMPLE_GOAL}</span>}
														{...methods.register('goal')}
														validationState={
															isSubmitted && errors.goal ? 'invalid' : undefined
														}
														errorPlacement="inside"
														errorMessage={
															isSubmitted && errors.goal
																? 'Обязательно для заполнения'
																: undefined
														}
													/>
												</FormRow>

												<div className={styles.scoreField}>
													<Controller
														name="scoreResult"
														control={methods.control}
														render={({ field }) => (
															<ResultScoreSelect
																value={field.value as string}
																placeholder="Выберите оценку"
																onChange={field.onChange}
															/>
														)}
													/>
												</div>
											</div>

											<FormRow label={GOAL_PLAN} className={`${styles.info__row} ${styles.top}`}>
												<TextArea
													size="xl"
													hasClear
													minRows={4}
													placeholder={STEPS_PLAN}
													{...methods.register('goalPlan')}
													validationState={
														isSubmitted && errors.goalPlan ? 'invalid' : undefined
													}
													errorPlacement="inside"
													errorMessage={
														isSubmitted && errors.goalPlan
															? 'Обязательно для заполнения'
															: undefined
													}
												/>
											</FormRow>

											<div className={styles.info__btn_group}>
												{active !== 0 && (
													<Button onClick={prevStep} size="xl">
														<Icon data={ArrowLeft} size={18} />
														{BACK}
													</Button>
												)}
												<Button
													size="xl"
													type="submit"
													//disabled={!isValid}
													// view={isValid ? 'action' : 'raised'}
													className={styles.info__btn_group__save}
												>
													{FINAL_SAVE}
												</Button>
											</div>
										</div>
									</form>
								</FormProvider>
							</div>

							<div className={styles.helpers}>
								<div className={`${styles.help} ${isHelpUpperVisible ? '' : styles.hidden}`}>
									<div className={styles.help__title}>
										<CircleInfoFill className={styles.help__title__svg} />
										<h4>{HINTS}</h4>
										<Button onClick={toggleHelpUpperVisibility} view="flat">
											{' '}
											{isHelpUpperVisible ? <ChevronUp /> : <ChevronDown />}
										</Button>
									</div>
									<div className={`${styles.help__list} ${isHelpUpperVisible ? '' : styles.hidden}`}>
										<div>
											<h4>{EVALUATE_ACTIVITY}</h4>
											<span>{SCALE}</span>
											<h4>{SATISFACTION_ACTIVITY}</h4>
											<span>{SCALE_SATISFACTION}</span>
										</div>
									</div>
								</div>
								<div className={`${styles.help} ${isHelpLowerVisible ? '' : styles.hidden}`}>
									<div className={styles.help__title}>
										<CircleInfoFill className={styles.help__title__svg} />
										<h4>{HINTS}</h4>
										<Button onClick={toggleHelpLowerVisibility} view="flat">
											{' '}
											{isHelpLowerVisible ? <ChevronUp /> : <ChevronDown />}
										</Button>
									</div>
									<div className={`${styles.help__list} ${isHelpLowerVisible ? '' : styles.hidden}`}>
										<ol>
											{HINTS_QUESTIONS}
											<ul>
												<li>{ACHIEVEMENTS}</li>
												<li>{RESULT}</li>
												<li>{DEADLINES}</li>
											</ul>
										</ol>
									</div>
								</div>
							</div>
						</div>
					)}
				</>
			)}
		</>
	);
};
